import { ReactElement, useCallback, useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { ProcurationFeeRuleResponse, procurationFeeRulesValidateDelete, procurationFeeRulesCreateDeleteCr } from '@pmp/api';
import { usePmpFetch } from '@pmp/hooks';
import { CrudMode } from '@ohpen/enso-ui';

import { ProcurationFeeRulesTabProps } from '../interfaces';
import { getInitialState, reducer } from '../reducer';
import { ProcurationFeeRulesList } from './ProcurationFeeRulesList';
import { ProcurationFeeRulesCrudForm } from './ProcurationFeeRulesCrudForm';
import { ConfirmCrDeleteModal } from '@pmp/components';

export const ProcurationFeeRulesTab = ({ valueLists }: ProcurationFeeRulesTabProps): ReactElement => {
  const [isFetching, , data, , fetchData] = usePmpFetch('procurationFeeRulesGetAllProcurationFeeRules');
  const [state, dispatch] = useReducer(reducer<ProcurationFeeRuleResponse>, getInitialState<ProcurationFeeRuleResponse>());
  const { t } = useTranslation();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCreate = useCallback(() => {
    dispatch({ type: 'openCrud', record: null, mode: CrudMode.CREATE });
  }, []);

  const onView = useCallback((record: ProcurationFeeRuleResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.VIEW });
  }, []);

  const onEdit = useCallback((record: ProcurationFeeRuleResponse) => {
    dispatch({ type: 'openCrud', record, mode: CrudMode.EDIT });
  }, []);

  const onCloseCrud = useCallback(() => {
    dispatch({ type: 'closeCrud' });
  }, []);

  const onDelete = useCallback((record: ProcurationFeeRuleResponse) => {
    dispatch({ type: 'openDeleteModal', record });
  }, []);

  const onCloseDeleteModal = useCallback(() => {
    dispatch({ type: 'closeDeleteModal' });
  }, []);

  return (
    <>
      <ProcurationFeeRulesList
        data={data ?? []}
        isFetching={isFetching}
        valueLists={valueLists}
        onCreate={onCreate}
        onView={onView}
        onEdit={onEdit}
        onDelete={onDelete}
      />
      {state.crud.open && state.crud.mode && (
        <ProcurationFeeRulesCrudForm
          mode={state.crud.mode}
          record={state.crud.record}
          valueLists={valueLists}
          refreshTable={fetchData}
          onClose={onCloseCrud}
        />
      )}
      <ConfirmCrDeleteModal
        open={state.deleteModal.open}
        records={state.deleteModal.record ? [state.deleteModal.record] : []}
        idKey="id"
        nameKey="name"
        entityLabel={t('lender.procurationFees.labels.procurationFeeRule')}
        entityLabelPlural={t('lender.procurationFees.labels.procurationFeeRules')}
        getPrecheck={procurationFeeRulesValidateDelete}
        createDeleteChangeRequest={procurationFeeRulesCreateDeleteCr}
        refreshTable={fetchData}
        onClose={onCloseDeleteModal}
      />
    </>
  );
};
